import React from 'react'

import {
    Checkbox,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'

import { Address, Company, PermissionRole } from '../../../models'
import { HeadCell, RowData } from '../../../components'

const TABLE_HEAD_CELLS: HeadCell<RowData>[] = [
    { id: 'check', label: '', align: 'left', disablePadding: false, width: 50 },
    {
        id: 'name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
    {
        id: 'address',
        label: 'Address',
        align: 'left',
        disablePadding: false,
        width: 250,
    },
    {
        id: 'permissions',
        label: 'Current Permission Roles',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
]

const useLocalStyles = makeStyles((theme) => ({
    tableWrapper: {
        flex: 1,
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 380px)',
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
    },
    table: {
        minWidth: 750,
        tableLayout: 'fixed',
    },
    tableBody: {
        height: 'auto',
    },
    tableRow: {
        height: 48,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    emptySpace: {
        flex: 1,
        minHeight: 0,
    },
}))

interface Props {
    companies: Company[]
    selectedCompanies: Record<string, boolean>
    onSelectCompany: (companyId: string, checked: boolean) => void
    onSelectAll: (checked: boolean) => void
    isAllSelected: boolean
    selectedCount: number
    disabledCompanies?: Set<string>
    userCompanyPermissionMap?: Map<string, PermissionRole[]>
    hidePermissionRoles?: boolean
}

export const CompanyTable = ({
    companies,
    selectedCompanies,
    onSelectCompany,
    onSelectAll,
    isAllSelected,
    selectedCount,
    disabledCompanies,
    userCompanyPermissionMap,
    hidePermissionRoles = false,
}: Props) => {
    const localClasses = useLocalStyles()

    const visibleHeadCells = TABLE_HEAD_CELLS.filter(
        (cell) => !hidePermissionRoles || cell.id !== 'permissions',
    )

    return (
        <TableContainer component={Paper} className={localClasses.tableWrapper}>
            <Table className={localClasses.table} size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {visibleHeadCells.map((cell) => (
                            <TableCell
                                key={cell.id}
                                align={
                                    cell.id === 'check' ? 'left' : cell.align
                                }
                                padding={
                                    cell.id === 'check'
                                        ? 'checkbox'
                                        : cell.disablePadding
                                        ? 'none'
                                        : 'default'
                                }
                                className={localClasses.stickyHeader}
                                style={{ width: cell.width }}
                            >
                                {cell.id === 'check' ? (
                                    <Checkbox
                                        indeterminate={
                                            selectedCount > 0 && !isAllSelected
                                        }
                                        checked={isAllSelected}
                                        onChange={(e) =>
                                            onSelectAll(e.target.checked)
                                        }
                                        color="primary"
                                    />
                                ) : (
                                    cell.label
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody className={localClasses.tableBody}>
                    {companies.map((company) => {
                        const isDisabled = disabledCompanies?.has(
                            company.id.toString(),
                        )
                        const companyPermissions = userCompanyPermissionMap?.get(
                            company.id.toString(),
                        )

                        return (
                            <TableRow
                                key={`ROW_${company.id}`}
                                hover
                                className={localClasses.tableRow}
                            >
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={
                                            isDisabled ||
                                            selectedCompanies[company.id] ===
                                                true
                                        }
                                        onChange={(e) =>
                                            onSelectCompany(
                                                company.id.toString(),
                                                e.target.checked,
                                            )
                                        }
                                        color="primary"
                                        disabled={isDisabled}
                                    />
                                </TableCell>
                                <TableCell>{company.name}</TableCell>
                                <TableCell>
                                    <AddressCell
                                        address={
                                            company.primary_address ?? null
                                        }
                                    />
                                </TableCell>
                                {!hidePermissionRoles && (
                                    <TableCell>
                                        {companyPermissions?.length ? (
                                            <Typography variant="body2">
                                                {companyPermissions
                                                    .map((role) => role.name)
                                                    .join(', ')}
                                            </Typography>
                                        ) : (
                                            <div />
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            <div className={localClasses.emptySpace} />
        </TableContainer>
    )
}

const AddressCell = ({ address }: { address: Address | null }) => {
    if (!address) {
        return <Typography variant="subtitle2">N/A</Typography>
    }

    return (
        <div>
            <Typography variant="subtitle2">{address.address1}</Typography>
            <Typography variant="subtitle2">
                {address.city}, {address.state} {address.zip_code}
            </Typography>
        </div>
    )
}
